import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { MultiSelect } from 'app/shared/select';
import { useSelector } from 'react-redux';
import GmModal from 'app/shared/modal/modal';
import AddMailboxModal from 'app/shared/add-mailbox-modal';
import { SimpleInput } from 'app/shared/input';
import { Icon } from 'assets/icons';
import cx from 'classnames';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './datepicker.css';
import styles from './sidebar.module.css';
import { DetailItem } from 'app/shared/info-section/item';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { Button } from 'app/shared/button';

export const SidebarForm = ({
  campaign,
  audiences = [],
  events = [],
  test_audience,
  is_test_audience_selected,
  mailbox,
  onChange = () => {},
  draft_status,
  errors,
  onSave,
  is_saving,
  is_save_disabled
}) => {
  const { determineSubscriptionPlanClass } = useSubscriptionService();
  const { tenant_id } = useSelector((state) => state.user_data);

  const [show_add_mailbox_modal, setShowAddMailboxModal] = useState(false);
  const [can_track_conversion, setCanTrackConversion] = useState(false);

  useEffect(() => {
    determineSubscriptionPlanClass({
      query_string: 'plan_code=campaign,startup,automation_plus&status=active'
    }).then((plan_class) => {
      setCanTrackConversion(plan_class && plan_class !== 'flex');
    });
  }, [tenant_id]);

  const get_selected_audiences = (audience_list) => {
    const audienceArray = [];
    for (const audience of audience_list) {
      audiences.find((aud) => {
        return aud.id === audience ? audienceArray.push(aud) : '';
      });
    }
    return audienceArray;
  };

  const [selected_audiences, setSelectedAudiences] = useState([]);
  useEffect(() => {
    if (campaign.mailing_lists && audiences) {
      const found = get_selected_audiences(campaign.mailing_lists);
      setSelectedAudiences(
        found?.map((audience) => ({
          label: audience.name,
          value: audience.id
        }))
      );
      return;
    }

    setSelectedAudiences([]);
  }, [audiences, campaign]);

  const handleAudienceSelection = (list) => {
    setSelectedAudiences(list);
    onChange('mailing_lists', list?.map((a) => a.value) || []);
  };

  const handleTestAudienceSelection = (is_test_selected) => {
    if (!test_audience) {
      toast.error('No test audience found');
      return;
    }

    if (is_test_selected) {
      setSelectedAudiences([]);
      onChange('mailing_lists', []);
    } else {
      const { name, id } = test_audience;
      setSelectedAudiences([{ label: name, value: id }]);
      onChange('mailing_lists', [id]);
    }
  };

  const handleMailboxAdded = (email) => {
    mailbox.emails.push(email);
  };

  return (
    <div className={styles.formWrapper}>
      {/* Mode toggle */}
      <div className={styles.modeToggle}>
        <div className={styles.modePill}>
          <div
            className={styles.modeIndicator}
            style={{ transform: is_test_audience_selected ? 'translateX(100%)' : 'translateX(0)' }}
          />
          <button
            className={`${styles.modeBtn} ${
              !is_test_audience_selected ? styles.modeBtnActive : ''
            }`}
            onClick={() => handleTestAudienceSelection(is_test_audience_selected)}
            type="button"
          >
            Live
          </button>
          <button
            className={`${styles.modeBtn} ${is_test_audience_selected ? styles.modeBtnActive : ''}`}
            onClick={() => handleTestAudienceSelection(is_test_audience_selected)}
            type="button"
          >
            Test
          </button>
        </div>
        {draft_status && (
          <span className={styles.draftPill}>
            <span className={styles.draftDot} />
            {draft_status}
          </span>
        )}
      </div>

      <div className={styles.fields}>
        {/* Name + preview */}
        <div className={styles.group}>
          <DetailItem title="Name" required>
            <SimpleInput
              className={styles.textInput}
              wrapperClassName={styles.formGroup}
              input_id="campaign_name"
              type="text"
              value={campaign.name}
              onInput={(e) => onChange('name', e)}
            />
          </DetailItem>
          <DetailItem title="Preview text">
            <SimpleInput
              className={styles.textInput}
              wrapperClassName={styles.formGroup}
              input_id="campaign_preview_text"
              value={campaign.preview_text}
              onInput={(e) => onChange('preview_text', e)}
            />
          </DetailItem>
        </div>

        {/* Sender */}
        <div className={styles.group}>
          <GridRow num_of_columns={5}>
            <GridColumn span={2}>
              <DetailItem title="Sender Name" required>
                <SimpleInput
                  className={styles.textInput}
                  wrapperClassName={styles.formGroup}
                  input_id="sender_name"
                  value={campaign.sender_name}
                  onInput={(e) => onChange('sender_name', e)}
                  error={errors.sender_name}
                />
              </DetailItem>
            </GridColumn>
            <GridColumn span={3}>
              <DetailItem title="Sender Email" required>
                <div className={styles.senderEmailInputRow}>
                  <div className={styles.senderEmailSelect}>
                    <MultiSelect
                      options={(mailbox?.emails || []).map((e) => ({ label: e, value: e }))}
                      onChange={(v) => onChange('sender_email', v.value)}
                      value={{ value: campaign.sender_email, label: campaign.sender_email }}
                      error={errors.sender_email}
                      className={styles.noMargin}
                    />
                  </div>
                  <button
                    className={styles.addEmailBtn}
                    onClick={() => setShowAddMailboxModal(true)}
                    type="button"
                    title="Add sender email"
                  >
                    <Icon name="add_circle" width={16} height={16} />
                  </button>
                </div>
              </DetailItem>
            </GridColumn>
          </GridRow>
        </div>

        {/* Audiences */}
        <DetailItem title="Audiences" required>
          <MultiSelect
            options={audiences
              .filter((a) => !a.is_test_audience)
              .map((a) => ({ label: a.name, value: a.id }))}
            onChange={handleAudienceSelection}
            value={selected_audiences}
            isMulti
            error={errors.mailing_lists}
          />
        </DetailItem>

        {/* Schedule + Conversion Event */}
        {!is_test_audience_selected && (
          <GridRow num_of_columns={2}>
            <GridColumn span={1}>
              <DetailItem title="Schedule" required>
                <DatePicker
                  isClearable
                  showTimeSelect
                  placeholderText="Pick a date & time"
                  dateFormat="MMM d, yyyy h:mm aa"
                  className="gm-input"
                  wrapperClassName={styles.datePickerWrapper}
                  minDate={new Date()}
                  selected={campaign?.schedule?.date ? new Date(campaign.schedule.date) : null}
                  onChange={(v) => onChange('schedule', { date: v })}
                  popperClassName={styles.date_picker}
                  popperPlacement="bottom-start"
                  popperModifiers={[
                    { name: 'offset', options: { offset: [0, 4] } },
                    { name: 'preventOverflow', options: { boundary: 'clippingParents' } }
                  ]}
                />
                {errors.schedule && (
                  <span className={cx(styles.error, 'mb-1 d-inline-block')}>{errors.schedule}</span>
                )}
              </DetailItem>
            </GridColumn>
            {can_track_conversion && (
              <GridColumn span={1}>
                <DetailItem title="Conversion Event">
                  <MultiSelect
                    options={events}
                    onChange={(ev) =>
                      onChange('conversion_event', ev ? ev.value.split(':')[0] : '')
                    }
                    isClearable
                    value={
                      events.find((e) => e.value === `${campaign.conversion_event}:${tenant_id}`) ||
                      {}
                    }
                  />
                </DetailItem>
              </GridColumn>
            )}
          </GridRow>
        )}

        {/* Inbox preview */}
        <div className={styles.inboxPreview}>
          <p className={styles.inboxPreviewLabel}>Inbox preview</p>
          <div className={styles.inboxCard}>
            <div className={styles.inboxAvatar}>
              {(campaign.sender_name || 'S').charAt(0).toUpperCase()}
            </div>
            <div className={styles.inboxBody}>
              <div className={styles.inboxTop}>
                <span className={styles.inboxSender}>{campaign.sender_name || 'Sender name'}</span>
                <span className={styles.inboxTime}>Just now</span>
              </div>
              <div className={styles.inboxSubject}>
                {campaign.subject || 'Your email subject line'}
              </div>
              <div className={styles.inboxSnippet}>
                {campaign.preview_text || 'Preview text will appear here…'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send button */}
      <div className={styles.submitForm}>
        <Button
          className={styles.sendButton}
          onClick={onSave}
          disabled={is_save_disabled || !!draft_status}
          loading={is_saving}
          text={is_test_audience_selected ? 'Send Test' : 'Send Campaign'}
        />
      </div>

      <GmModal
        title="Add sender email"
        show_title={true}
        show_modal={show_add_mailbox_modal}
        onClose={() => setShowAddMailboxModal(false)}
      >
        <AddMailboxModal mailbox={mailbox} onSuccess={handleMailboxAdded} />
      </GmModal>
    </div>
  );
};
